import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaSpinner, FaUser, FaUsers, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import LookupLayout from '../layout/LookupLayout';
import DataApi from '../../api/DataApi';
import { pickRecordId } from '../../utils/dashboardRunUserLabel';
import '../dashboard/css/DashboardManager.css';

const formatUserLabel = (u) => {
  const name = `${u.firstname || ''} ${u.lastname || ''}`.trim();
  if (name && u.email) return `${name} (${u.email})`;
  return name || u.email || u.username || u.id;
};

const EntityAssignUsersModal = ({
  show,
  entity,
  onClose,
  onSaved,
  apiResource,
  entityTypeLabel = 'item',
  assignmentIdField = 'entity_id',
  exclusiveAssignment = true,
  maxAssignedPerUser = 0,
  introText,
  emptyHint,
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignmentByUserId, setAssignmentByUserId] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lookupKey, setLookupKey] = useState(0);

  const currentEntityId = pickRecordId(entity) || entity?.id;
  const perUserLimit =
    Number.isFinite(Number(maxAssignedPerUser)) && Number(maxAssignedPerUser) > 0
      ? Number(maxAssignedPerUser)
      : 0;

  const entityDisplayName = (e) =>
    (e?.label || e?.home_label || e?.report_label || e?.name || '').trim() ||
    `this ${entityTypeLabel}`;

  const getAssignmentEntityId = (row) =>
    row?.[assignmentIdField] ?? row?.entity_id ?? row?.home_id ?? row?.dashboard_id ?? row?.report_id;

  const labelForAssignmentRow = (row) =>
    (row?.entity_label || row?.home_label || row?.report_label || row?.entity_name || row?.home_name || '').trim() ||
    `another ${entityTypeLabel}`;

  const enrichUser = (userId, label, map) => {
    const assignment = map?.[String(userId)];
    const otherId = getAssignmentEntityId(assignment);
    const otherEntity =
      exclusiveAssignment && otherId && String(otherId) !== String(currentEntityId)
        ? { id: otherId, label: labelForAssignmentRow(assignment) }
        : null;
    return {
      id: userId,
      label,
      otherEntity,
    };
  };

  const countOtherAssignments = (rows) =>
    (rows || []).filter(
      (row) => String(getAssignmentEntityId(row)) !== String(currentEntityId)
    ).length;

  useEffect(() => {
    if (!show) {
      setSelectedUsers([]);
      setAssignmentByUserId({});
      setError(null);
      setLoading(false);
      return;
    }

    const entityId = pickRecordId(entity) || entity?.id;
    if (!entityId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLookupKey((k) => k + 1);

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const api = new DataApi(apiResource);

        const usersPromise = api.get(`/${entityId}/users`);
        const mapPromise = api
          .get('/user-assignments')
          .then((res) => {
            const rows = res?.data?.data || [];
            const map = {};
            for (const row of rows) {
              if (row?.user_id) map[String(row.user_id)] = row;
            }
            return map;
          })
          .catch((err) => {
            console.warn('Could not load user-assignment map:', err);
            return {};
          });

        const [usersRes, map] = await Promise.all([usersPromise, mapPromise]);
        if (cancelled) return;

        setAssignmentByUserId(map);
        const rows = usersRes?.data?.data || [];
        setSelectedUsers(
          rows.map((u) => enrichUser(u.id, formatUserLabel(u), map))
        );
      } catch (err) {
        if (cancelled) return;
        console.error(`Error loading ${entityTypeLabel} assignments:`, err);
        setError(
          err?.response?.data?.message || 'Failed to load assigned users.'
        );
        setSelectedUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, entity?.id, apiResource, exclusiveAssignment, assignmentIdField, entityTypeLabel]);

  const usersToTransfer = useMemo(
    () => (exclusiveAssignment ? selectedUsers.filter((u) => u.otherEntity) : []),
    [exclusiveAssignment, selectedUsers]
  );

  const handleUserSelect = async (userId, userLabel, item) => {
    const uid =
      (item && pickRecordId({ id: item.id })) ||
      pickRecordId({ id: userId }) ||
      String(userId || '').trim();
    const label = (userLabel || '').trim();
    if (!uid || !label) return;
    if (selectedUsers.some((u) => String(u.id) === String(uid))) {
      setError('User is already assigned to this ' + entityTypeLabel + '.');
      setTimeout(() => setError(null), 3000);
      setLookupKey((k) => k + 1);
      return;
    }

    let map = assignmentByUserId;
    let assignmentRows = [];
    try {
      const api = new DataApi(apiResource);
      const path = exclusiveAssignment
        ? `/users/${uid}/assignment`
        : `/users/${uid}/assignments`;
      const res = await api.get(path);
      assignmentRows = exclusiveAssignment
        ? res?.data?.data
          ? [res.data.data]
          : []
        : res?.data?.data || [];
      if (assignmentRows.length) {
        const merged = { ...map };
        for (const row of assignmentRows) {
          if (row?.user_id) merged[String(row.user_id)] = row;
        }
        map = merged;
        setAssignmentByUserId(map);
      }
    } catch (err) {
      console.error('Error loading user assignment:', err);
    }

    if (perUserLimit > 0 && !exclusiveAssignment) {
      const otherCount = countOtherAssignments(assignmentRows);
      if (otherCount >= perUserLimit) {
        setError(
          `${label} is already assigned to ${otherCount} other ${entityTypeLabel}(s). Each user can have at most ${perUserLimit} assigned ${entityTypeLabel}(s). Remove them from another ${entityTypeLabel} first.`
        );
        setLookupKey((k) => k + 1);
        return;
      }
    }

    const entry = enrichUser(uid, label, map);
    setSelectedUsers((prev) => [...prev, entry]);

    if (entry.otherEntity) {
      setError(
        `${label} is already assigned to "${entry.otherEntity.label}". Saving will remove them from that ${entityTypeLabel} and assign them here.`
      );
    } else {
      setError(null);
    }
    setLookupKey((k) => k + 1);
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => String(u.id) !== String(userId)));
    setError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentEntityId) return;

    const transfers = usersToTransfer;
    if (transfers.length > 0) {
      const name = entityDisplayName(entity);
      const lines = transfers
        .map(
          (u) =>
            `• ${u.label}\n  Currently on: "${u.otherEntity.label}" → will move to "${name}"`
        )
        .join('\n\n');
      const ok = window.confirm(
        exclusiveAssignment
          ? `Each user can only have one assigned ${entityTypeLabel}.\n\nThe following will be removed from their current ${entityTypeLabel} and assigned to "${name}":\n\n${lines}\n\nContinue?`
          : `Save user assignments for "${name}"?\n\n${lines}\n\nContinue?`
      );
      if (!ok) return;
    }

    const entityId = pickRecordId(entity) || entity?.id;
    if (!entityId) {
      setError(`Missing ${entityTypeLabel} id. Close and try again.`);
      return;
    }

    const userIds = selectedUsers
      .map((u) => String(u.id || '').trim())
      .filter(Boolean);

    try {
      setSaving(true);
      setError(null);
      const api = new DataApi(apiResource);
      const res = await api.put(`/${entityId}/users`, { user_ids: userIds });

      if (res?.data?.success === false) {
        throw new Error(res?.data?.message || 'Failed to save assignments.');
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error(`Error saving ${entityTypeLabel} assignments:`, err);
      setError(err?.response?.data?.message || err?.message || 'Failed to save assignments.');
    } finally {
      setSaving(false);
    }
  };

  if (!show || !entity) return null;

  const name = entityDisplayName(entity);
  const defaultIntro =
    exclusiveAssignment
      ? `Each user can only be assigned to one ${entityTypeLabel} at a time. Only the owner, assigned users, and admins can view a ${entityTypeLabel}.`
      : perUserLimit > 0
        ? `Assign users who may view this ${entityTypeLabel} (in addition to the owner and admins). Each user can be assigned to at most ${perUserLimit} ${entityTypeLabel}(s) across the org.`
        : `Assign users who may view this ${entityTypeLabel} (in addition to the owner and admins). Users not assigned cannot access it unless they own it.`;

  const defaultEmpty =
    exclusiveAssignment
      ? `No users assigned yet. Only the owner and admins can view this ${entityTypeLabel}.`
      : `No users assigned yet. Only the owner and admins can view this ${entityTypeLabel}.`;

  const modalContent = (
    <div className="entity-assign-modal-scope dashboard-manager">
      <div className="dashboard-modal-overlay" onClick={onClose}>
      <div
        className="dashboard-modal dashboard-modal-wide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-modal-header">
          <h2>
            <FaUsers className="dashboard-modal-title-icon" /> Assign users — {name}
          </h2>
          <button type="button" className="dashboard-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSave} className="dashboard-modal-form">
          <p className="dashboard-form-hint dashboard-assign-intro">
            {introText || defaultIntro}
          </p>

          {usersToTransfer.length > 0 && (
            <div className="dashboard-assign-notice" role="status">
              <FaExclamationTriangle className="me-1" aria-hidden />
              {usersToTransfer.length === 1
                ? '1 user will be moved from another assignment when you save.'
                : `${usersToTransfer.length} users will be moved from other assignments when you save.`}
            </div>
          )}

          {error && (
            <div className="dashboard-form-error" role="alert">
              {error}
            </div>
          )}

          <div className="dashboard-form-group">
            <label htmlFor="entity-assign-user-lookup">
              <FaUser className="me-1" /> Add user
            </label>
            <LookupLayout
              key={lookupKey}
              objectName="user"
              searchFields="firstname,lastname,email,username"
              displayFields="firstname,lastname,email"
              value=""
              defaultLabel=""
              onChange={handleUserSelect}
              placeholder="Search users by name or email…"
              disabled={saving || loading}
              dropdownZIndex={10100}
            />
          </div>

          <div className="dashboard-form-group">
            <label>Assigned users ({selectedUsers.length})</label>
            {loading ? (
              <div className="dashboard-assign-loading">
                <FaSpinner className="fa-spin" /> Loading…
              </div>
            ) : selectedUsers.length === 0 ? (
              <p className="dashboard-form-hint mb-0">{emptyHint || defaultEmpty}</p>
            ) : (
              <ul className="dashboard-assign-user-list">
                {selectedUsers.map((u) => (
                  <li key={u.id}>
                    <div className="dashboard-assign-user-info">
                      <span>{u.label}</span>
                      {u.otherEntity && (
                        <span className="dashboard-assign-transfer-warning">
                          Currently on &quot;{u.otherEntity.label}&quot; — will be removed when
                          saved
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="dashboard-assign-remove-btn"
                      onClick={() => handleRemoveUser(u.id)}
                      title="Remove from this assignment"
                      disabled={saving}
                    >
                      <FaTrash />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="dashboard-modal-actions">
            <button type="button" className="dashboard-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="dashboard-save-btn" disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save assignments'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EntityAssignUsersModal;
